import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import map from 'lodash/map';
import slice from 'lodash/slice';
import { formatEventLogs, getColumnConfig } from './helpers';
import EventCategory from './EventCategory';
import { ITEMS_PER_PAGE } from './constants';
import './style.scss';
import { GeneralPagination, LoadingStatusWrapper } from 'sharedComponents';
import { sortingDirections, GeneralTable } from 'sharedComponents/GeneralTable';
import CloseEventDialogComponent from './CloseEventDialogComponent';
import { useNewCapability } from 'sharedHooks';
import { apiCallStatus } from 'helpers/constants';
import { clearExpandedEventGroup } from '../actions';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';


const EventLogsComponent = (props) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const { showReportIssue } = useReportIssue();
    const selectedCategoryRef = useRef(null);

    const [closeEventPopupVisible, setCloseEventPopupVisible] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [expandedEventCategory, setExpandedEventCategory] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [isEventFiltered, setIsEventFiltered] = useState(false);
    const [eventGroup, setEventGroup] = useState(null);

    const showCloseEventPopup = (event) => {
        setCloseEventPopupVisible(true);
        setSelectedEvent(event);
        setSelectedEventId(event?.eventId);
    };

    const {
        eventLogs = [],
        filters = { groupByNames: false, dateSelection: {}, dateKind: 0 },
        selectedAssets = [],
        eventLogLoadingStatus,
        eventClosingLoadingStatus,
        eventGroupName,
        eventClosingErrorData
    } = props.model || {};

    const {
        getEventLogs,
        closeEventLog,
        clearClosingError,
    } = props.actions || {};

    const {
        isSS,
        assistanceCapability,
        setSelectedEventPage,
        setSelectedEventItemPage,
        selectedEventCategory,
        selectedEventItemPage
    } = props;

    const [eventLogData, setEventLogData] = useState(formatEventLogs(
        eventLogs,
        selectedAssets,
        isSS,
        filters.groupByNames,
        translate
    ));
    const [columnConfig, setColumnConfig] = useState(getColumnConfig(
        isSS,
        filters.groupByNames,
        translate,
        showCloseEventPopup,
        props.setSelectedEvent,
        assistanceCapability,
        filters.dateKind
    ));
    const [selectedPage, setSelectedPage] = useState(1);
    const shouldRequestEventLogs = useNewCapability().hasEventListCapability();
    const lastItemPageRef = useRef(1);
    const shouldRestoreItemPageRef = useRef(false);

    const getPage = (list, page, itemsPerPage) => {
        if (!Array.isArray(list) || list.length === 0) { return []; }
        if (page < 1 || itemsPerPage < 1) { return list; }
        const startIndex = itemsPerPage * (page - 1);
        const endIndex = startIndex + itemsPerPage;
        return slice(list, startIndex, endIndex < list.length ? endIndex : list.length);
    };

    const findEventGroupByEventId = (eventId) => {
        if (!Array.isArray(eventLogData)) { return null; }
        return eventLogData.find((group) =>
            Array.isArray(group?.events) && group.events.some((event) => event?.eventId === eventId)
        );
    };
    useEffect(() => {
        setSelectedEvents(selectedEventCategory?.messageText);
        setSelectedEventId(selectedEventCategory?.eventId);
    }, [selectedEventCategory]);

    useEffect(() => {
        const newTimer = setTimeout(() => {
            selectedAssets?.length &&
                filters?.dateSelection?.from &&
                filters?.dateSelection?.to &&
                shouldRequestEventLogs &&
                typeof getEventLogs === 'function' &&
                getEventLogs(selectedAssets,
                    filters,
                    translate('ABB.Powertrain.Frontend.eventListTruncatedWarningMessage'));

        }, filters?.eventName?.length > 0 ? 700 : 0);

        return () => {
            clearTimeout(newTimer);
        };
    }, [
        filters.dateSelection,
        filters.dateKind,
        filters.eventStatus,
        filters.eventTypes,
        filters.eventName,
        filters.causeOfEvent,
        filters.withCommentsOnly,
        filters.withDataLoggersOnly,
        JSON.stringify(selectedAssets)
    ]);
    const validateAndUpdatePage = (dataLength) => {
        const itemsPerPage = ITEMS_PER_PAGE || 1;
        const totalPages = Math.max(1, Math.ceil(dataLength / itemsPerPage));
        const validSelectedPage = Math.max(1, selectedPage || 1);

        if (validSelectedPage <= totalPages) { return; }

        const newPage = Math.min(Math.max(1, props.selectedEventPage || 1), totalPages);
        setSelectedPage(newPage);
        if (typeof setSelectedEventPage === 'function') {
            setSelectedEventPage(newPage);
        }
    };

    const cleanupExpandedCategories = (formattedData) => {
        if (!formattedData?.length) {
            setExpandedEventCategory(prev => prev.length > 0 ? [] : prev);
            return;
        }

        const isCategoryValid = (category) => {
            return category && formattedData.some(eventLog =>
                eventLog?.eventGroupName === category
            );
        };

        setExpandedEventCategory(prev => {
            if (!Array.isArray(prev) || prev.length === 0) { return prev; }

            const validCategories = prev.filter(isCategoryValid);
            return validCategories.length === prev.length ? prev : validCategories;
        });
    };

    useEffect(() => {
        const formattedEventLogData = formatEventLogs(
            eventLogs,
            selectedAssets,
            isSS,
            filters.groupByNames,
            translate
        );
        setEventLogData(formattedEventLogData);

        validateAndUpdatePage(formattedEventLogData?.length || 0);
        cleanupExpandedCategories(formattedEventLogData);
    }, [eventLogs, filters]);

    useEffect(() => {
        setColumnConfig(getColumnConfig(
            isSS,
            filters.groupByNames,
            translate,
            showCloseEventPopup,
            props.setSelectedEvent,
            assistanceCapability,
            filters.dateKind
        ));
    }, [isSS, filters.groupByNames, filters.dateKind]);
    useEffect(() => {
        if (eventClosingLoadingStatus === apiCallStatus.SUCCESS &&
            selectedAssets?.length &&
            typeof getEventLogs === 'function' &&
            filters?.dateSelection?.from &&
            filters?.dateSelection?.to) {
            // Trigger re-fetch via existing debounced mechanism
            getEventLogs(
                selectedAssets,
                filters,
                translate('ABB.Powertrain.Frontend.eventListTruncatedWarningMessage')
            );
        }
    }, [eventClosingLoadingStatus]);
    const prevEventLogsRef = useRef(eventLogs);

    const calculateEventItemPage = (eventGroup, eventId) => {
        const eventIndex = eventGroup?.events?.findIndex(event => event?.eventId === eventId) ?? -1;
        if (eventIndex === -1) { return 1; }
        const itemsPerPage = ITEMS_PER_PAGE || 1;
        return Math.floor(eventIndex / itemsPerPage) + 1;
    };

    const expandEventGroup = (groupName) => {
        setExpandedEventCategory(prev => {
            if (!Array.isArray(prev) || prev.includes(groupName)) { return prev; }
            return [...prev, groupName];
        });
    };

    const closeEventHandler = (closingReason, comment) => {
        if (!Array.isArray(eventLogData) || !selectedEvent?.eventId) { return; }
        if (typeof closeEventLog !== 'function') { return; }

        const eventGroup = findEventGroupByEventId(selectedEvent.eventId);
        if (!eventGroup?.eventGroupName) { return; }
        closeEventLog(selectedEvent, closingReason, comment);

        const itemPage = calculateEventItemPage(eventGroup, selectedEvent.eventId);
        lastItemPageRef.current = itemPage;
        shouldRestoreItemPageRef.current = true;

        expandEventGroup(eventGroup.eventGroupName);

        if (typeof setSelectedEventItemPage === 'function') {
            setSelectedEventItemPage(itemPage);
        }

        setSelectedEventId(selectedEvent?.eventId);
    };
    useEffect(() => {
        if (shouldRestoreItemPageRef.current && eventLogData?.length > 0) {
            if (selectedEventItemPage === (lastItemPageRef.current || 1)) {
                shouldRestoreItemPageRef.current = false;
            }
            // Auto-reset flag after timeout to prevent stuck state
            const resetTimer = setTimeout(() => {
                shouldRestoreItemPageRef.current = false;
            }, 2000);
            return () => clearTimeout(resetTimer);
        }
    }, [eventLogData, selectedEventItemPage]);
    useEffect(() => {
        const isValidGroupName = typeof eventGroupName === 'string' && eventGroupName.trim();
        if (!isValidGroupName) { return; }

        expandEventGroup(eventGroupName);

        if (dispatch && typeof clearExpandedEventGroup === 'function') {
            dispatch(clearExpandedEventGroup());
        }
    }, [eventGroupName, dispatch]);

    useEffect(() => {
        const shouldRefresh = !shouldRestoreItemPageRef.current &&
            eventLogLoadingStatus === 'success' &&
            eventLogs?.length > 0 &&
            JSON.stringify(prevEventLogsRef.current) !== JSON.stringify(eventLogs);

        if (shouldRefresh) {
            setSelectedEvents(null);
            const page = props?.selectedEventPage || 1;
            setSelectedPage(page);
            setSelectedEventPage(page);
            setIsEventFiltered(true);
            prevEventLogsRef.current = eventLogs;
        } else {
            setIsEventFiltered(false);
        }
    }, [eventLogs, props.setSelectedEvent]);
    const handlePageChange = (page) => {
        const validPage = Math.max(1, Number.parseInt(page, 10) || 1);
        setSelectedPage(validPage);
        if (typeof setSelectedEventPage === 'function') {
            setSelectedEventPage(validPage);
        }
        lastItemPageRef.current = validPage;
        setSelectedEvents(null);
    };

    const handleEventGroupToggle = (currentlyExpanded, groupName) => {
        if (!groupName) { return; }

        setEventGroup(groupName);
        setSelectedEventId(null);

        if (!Array.isArray(expandedEventCategory)) { return; }

        if (currentlyExpanded) {
            setExpandedEventCategory(prev => prev.filter(cat => cat !== groupName));
        } else {
            setExpandedEventCategory(prev => [...prev, groupName]);
        }
    };

    const getSelectedEventItemPage = (isSelectedEventInGroup) => {
        if (!isSelectedEventInGroup) { return null; }
        return shouldRestoreItemPageRef.current ? lastItemPageRef.current : selectedEventItemPage;
    };

    const renderEventCategory = (eventLogs) => {
        if (!eventLogs?.eventGroupName) { return null; }

        const isSelectedEventInGroup = eventLogs.events?.some((event) => event?.eventId === selectedEventId) ?? false;
        const isExpanded = (expandedEventCategory?.includes(eventLogs.eventGroupName) ?? false) || isSelectedEventInGroup;

        return (
            <EventCategory
                category={eventLogs.eventType}
                isExpanded={isExpanded}
                setIsExpanded={(currentlyExpanded) => handleEventGroupToggle(currentlyExpanded, eventLogs.eventGroupName)}
                key={eventLogs.eventGroupName}
                name={eventLogs.eventGroupName}
                ref={isSelectedEventInGroup ? selectedCategoryRef : null}
            >
                <GeneralTable
                    className='grouped-events'
                    tableKey='id'
                    scroll={{ x: 1200, y: null }}
                    columns={columnConfig}
                    data={Array.isArray(eventLogs?.events) ? eventLogs.events : []}
                    paginationProps={{ pageSize: ITEMS_PER_PAGE, hideOnSinglePage: true }}
                    initialSortingProps={{
                        column: isSS ? 'occurredOn' : 'time',
                        direction: sortingDirections.DESC
                    }}
                    requiredPaginateHandler={true}
                    setSelectedEventItemPage={setSelectedEventItemPage}
                    selectedEventItemPage={getSelectedEventItemPage(isSelectedEventInGroup)}
                    selectedEventId={selectedEventId}
                    isEventFiltered={isEventFiltered}
                />
            </EventCategory>
        );
    };

    const navigateToOuterPage = (groupIndex) => {
        const itemsPerPage = ITEMS_PER_PAGE || 1;
        const calculatedPage = Math.floor(groupIndex / itemsPerPage) + 1;

        if (selectedPage === calculatedPage) { return; }

        setSelectedPage(calculatedPage);
        if (typeof setSelectedEventPage === 'function') {
            setSelectedEventPage(calculatedPage);
        }
    };

    const navigateToInnerPage = (group, eventId) => {
        const itemPage = calculateEventItemPage(group, eventId);
        if (typeof setSelectedEventItemPage === 'function') {
            setSelectedEventItemPage(itemPage);
        }
    };

    useEffect(() => {
        if (!selectedEventId || !Array.isArray(eventLogData) || eventLogData.length === 0) { return; }

        const expandedGroupIndex = eventLogData.findIndex((eventLogs) =>
            eventLogs?.events?.some((event) => event?.eventId === selectedEventId)
        );

        if (expandedGroupIndex === -1) { return; }

        const expandedGroup = eventLogData[expandedGroupIndex];

        navigateToOuterPage(expandedGroupIndex);

        if (expandedGroup?.eventGroupName) {
            expandEventGroup(expandedGroup.eventGroupName);
            navigateToInnerPage(expandedGroup, selectedEventId);
        }
    }, [eventLogData, selectedEventId]);

    const renderEventGroups = () => {
        const hasEventData = Array.isArray(eventLogData) && eventLogData.length > 0;
        const content = hasEventData
            ? map(getPage(eventLogData, selectedPage, ITEMS_PER_PAGE), renderEventCategory).filter(Boolean)
            : <div className='event-log-no-information'>
                {translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')}
            </div>;

        return (
            <>
                <div className='detailed-info-event-groups'>{content}</div>
                <GeneralPagination
                    currentPage={selectedPage}
                    total={eventLogData?.length ?? 0}
                    pageSize={ITEMS_PER_PAGE}
                    onChange={handlePageChange}
                    hideOnSinglePage={true}
                />
            </>
        );
    };

    if (eventClosingErrorData?.loadingStatus === apiCallStatus.ERROR) {
        return <ErrorView
            errorCode={eventClosingErrorData.errorCode}
            onRetry={() => {
                if (typeof clearClosingError === 'function') {
                    clearClosingError();
                }
                if (selectedEvent?.eventId) {
                    setCloseEventPopupVisible(true);
                }
            }}
            onReportIssue={showReportIssue}
        />;
    }

    return (
        <div className='detailed-info-event-log-container'>
            <LoadingStatusWrapper loadingStatus={[eventLogLoadingStatus, eventClosingLoadingStatus]}>
                <CloseEventDialogComponent
                    show={closeEventPopupVisible}
                    close={() => { setCloseEventPopupVisible(false); }}
                    closeEventHandler={closeEventHandler}
                    eventGroup={eventGroup}
                    selectedEvents={selectedEvents}
                />

                {filters.groupByNames || isSS ?
                    renderEventGroups()
                    : <GeneralTable
                        className='ungrouped-events'
                        scroll={{ x: 1200, y: null }}
                        tableKey='id'
                        columns={columnConfig}
                        data={Array.isArray(eventLogData?.events) ? eventLogData.events : []}
                        emptyText={translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')}
                        paginationProps={{ pageSize: ITEMS_PER_PAGE, hideOnSinglePage: true }}
                        initialSortingProps={{
                            column: 'time',
                            direction: sortingDirections.DESC
                        }}
                        requiredPaginateHandler={true}
                        setSelectedEventItemPage={setSelectedEventItemPage}
                        selectedEventItemPage={selectedEventItemPage}
                        selectedEventId={selectedEventId}
                        isEventFiltered={isEventFiltered}
                    />
                }
            </LoadingStatusWrapper>
        </div>
    );
};

EventLogsComponent.propTypes = {
    model: PropTypes.shape({
        eventLogs: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Event ID
                eventGroupName: PropTypes.string, // Event group name
                eventType: PropTypes.string, // Event type
                events: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string, // Event ID
                        eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Event ID used in logic
                        time: PropTypes.string, // Event time
                        occurredOn: PropTypes.string, // Event occurrence time
                    })
                ), // Array of events in the group
            })
        ), // Array of event logs
        filters: PropTypes.shape({
            groupByNames: PropTypes.bool, // Whether to group events by names
            dateSelection: PropTypes.shape({
                from: PropTypes.string, // Start date of the selection
                to: PropTypes.string, // End date of the selection
            }),
            dateKind: PropTypes.number, // Date kind (e.g., UTC or local)
            eventStatus: PropTypes.string, // Selected event status
            eventTypes: PropTypes.arrayOf(PropTypes.string), // Array of selected event types
            eventName: PropTypes.string, // Search term for event name
            causeOfEvent: PropTypes.string, // Selected cause of event
            withCommentsOnly: PropTypes.bool, // Whether to filter by events with comments only
            withDataLoggersOnly: PropTypes.bool, // Whether to filter by events with data loggers only
        }), // Filters applied to the event log
        selectedAssets: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Asset ID
                name: PropTypes.string, // Asset name
            })
        ), // Array of selected assets
        organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Organization ID
        eventLogLoadingStatus: PropTypes.string, // Loading status for the event log
        eventClosingLoadingStatus: PropTypes.string, // Loading status for event closing
        eventClosingErrorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.number
        }),
        user: PropTypes.shape({
            email: PropTypes.string, // User's email address
            name: PropTypes.string, // User's name
        }), // User details
        eventGroupName: PropTypes.string, // Added validation for eventGroupName
    }), // Model containing state data
    actions: PropTypes.shape({
        getEventLogs: PropTypes.func, // Function to fetch event logs
        closeEventLog: PropTypes.func, // Function to close an event log
    }), // Actions object containing functions
    setSelectedEvent: PropTypes.func, // Function to set the selected event
    isSS: PropTypes.bool, // Whether the component is in SS mode
    assistanceCapability: PropTypes.bool, // Whether assistance capability is enabled
    selectedEventCategory: PropTypes.any, // Selected event category
    selectedEventPage: PropTypes.number, // Current selected event page
    setSelectedEventPage: PropTypes.func, // Function to set the selected event page
    setSelectedEventItemPage: PropTypes.func, // Function to set the selected event item page
    selectedEventItemPage: PropTypes.number, // Current selected event item page
};

export default EventLogsComponent;
